<!--
CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
SPDX-License-Identifier: LicenseRef-Cercle-Interne

Fichier : apps/web/src/shell/CartoucheUtilisateur.vue
Rôle : porte l'identité, le compteur de notifications et le menu du compte.
Référence : designsystem.md ; CIG-SPECIFICATION.md §5.3, §22.2.
-->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useSession } from '../lib/store.ts';

const session = useSession();
const ouvert = ref(false);
const racine = ref<HTMLElement | null>(null);

const nom = computed(() => session.profil?.displayName ?? session.profil?.sub ?? '');

/** Initiales, à défaut d'une image de profil : le fournisseur n'en sert pas. */
const initiales = computed(() =>
  nom.value
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => m[0]?.toUpperCase() ?? '')
    .join('') || '?',
);

const enAttente = computed(() => session.notifications.length);

function basculer(): void {
  ouvert.value = !ouvert.value;
}

function fermer(): void {
  ouvert.value = false;
}

/** Un clic hors du cartouche referme le menu, comme une touche d'échappement. */
function surClicExterieur(ev: MouseEvent): void {
  if (!ouvert.value) return;
  if (racine.value && !racine.value.contains(ev.target as Node)) fermer();
}

function surEchappement(ev: KeyboardEvent): void {
  if (ev.key === 'Escape') fermer();
}

onMounted(() => {
  document.addEventListener('pointerdown', surClicExterieur);
  window.addEventListener('keydown', surEchappement);
});
onUnmounted(() => {
  document.removeEventListener('pointerdown', surClicExterieur);
  window.removeEventListener('keydown', surEchappement);
});
</script>

<template>
  <div ref="racine" class="cartouche">
    <button
      class="declencheur"
      :aria-expanded="ouvert"
      aria-haspopup="menu"
      :aria-label="`Compte de ${nom}${enAttente ? `, ${enAttente} notification(s)` : ''}`"
      @click="basculer"
    >
      <span class="identite">
        <span class="nom">{{ nom }}</span>
        <span v-if="session.profil?.isAdmin" class="role">Administrateur</span>
        <span v-else-if="session.profil?.groups.includes('/outsiders')" class="role">Externe</span>
      </span>

      <span class="medaillon">
        {{ initiales }}
        <!-- Cartouche du nombre de notifications en attente, sur le médaillon. -->
        <span v-if="enAttente > 0" class="pastille">{{ enAttente > 99 ? '99+' : enAttente }}</span>
      </span>
    </button>

    <Transition name="menu">
      <div v-if="ouvert" class="menu" role="menu">
        <RouterLink to="/notifications" class="entree" role="menuitem" @click="fermer">
          <span>Notifications</span>
          <span v-if="enAttente > 0" class="compteur">{{ enAttente }}</span>
        </RouterLink>
        <RouterLink to="/documentation" class="entree" role="menuitem" @click="fermer">
          Documentation
        </RouterLink>
        <hr class="separation">
        <form method="post" action="/auth/logout">
          <button class="entree entree--action" role="menuitem" type="submit">
            Se déconnecter
          </button>
        </form>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.cartouche { position: relative; }

.declencheur {
  display: flex;
  align-items: center;
  gap: var(--cig-espace-2);
  min-height: var(--cig-touche);
  padding: 0 0.35rem 0 var(--cig-espace-2);
  background: transparent;
  color: inherit;
  border-radius: var(--cig-rayon-pilule);
}
.declencheur:hover { background: rgb(255 255 255 / 10%); opacity: 1; }

.identite { display: flex; flex-direction: column; align-items: flex-end; line-height: 1.2; }
.nom { font-family: var(--cig-sans); font-size: 0.85rem; font-weight: 600; }
.role { font-family: var(--cig-sans); font-size: 0.68rem; opacity: 0.65; }

.medaillon {
  position: relative;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--cig-conteneur);
  color: var(--cig-encre);
  font-family: var(--cig-sans);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex: none;
}

/* La pastille est la seule note de couleur : elle signale une attente réelle. */
.pastille {
  position: absolute;
  top: -3px;
  right: -5px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  display: grid;
  place-items: center;
  border-radius: var(--cig-rayon-pilule);
  background: var(--cig-danger);
  color: #fff;
  font-family: var(--cig-sans);
  font-size: 0.62rem;
  font-weight: 700;
  box-shadow: 0 0 0 2px var(--cig-encre);
}

.menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 60;
  min-width: 15rem;
  padding: var(--cig-espace-1);
  background: var(--cig-conteneur);
  color: var(--cig-encre);
  border-radius: var(--cig-rayon);
  box-shadow: var(--cig-ombre-haute);
  transform-origin: top right;
}

.entree {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--cig-espace-2);
  width: 100%;
  min-height: 40px;
  padding: 0 var(--cig-espace-2);
  border-radius: var(--cig-rayon-petit);
  background: transparent;
  color: var(--cig-encre);
  font-family: var(--cig-sans);
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
}
.entree:hover { background: var(--cig-gris-clair); opacity: 1; }
.entree--action { color: var(--cig-danger); font-weight: 600; }

.compteur {
  min-width: 20px;
  padding: 0 6px;
  border-radius: var(--cig-rayon-pilule);
  background: var(--cig-gris-clair);
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
}

.separation { border: 0; border-top: 1px solid var(--cig-gris-clair); margin: var(--cig-espace-1) 0; }

/* Animation sobre : une ouverture qui se remarque sans se donner en spectacle. */
.menu-enter-active { transition: opacity 140ms ease, transform 160ms cubic-bezier(0.2, 0.8, 0.3, 1); }
.menu-leave-active { transition: opacity 110ms ease, transform 110ms ease; }
.menu-enter-from, .menu-leave-to { opacity: 0; transform: translateY(-6px) scale(0.97); }

@media (max-width: 48rem) { .identite { display: none; } }

@media (prefers-reduced-motion: reduce) {
  .menu-enter-active, .menu-leave-active { transition: none; }
}
</style>
